import HeadingArt from './HeadingArt';
import Icon from './Icon';
import PastelIllustration from './PastelIllustration';
import { useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DEMO, demoConversion, canonicalNumber } from './demoModel';
import { formatPublishedQuoteTime } from './publicSilverQuote';
import { numericDisplay, parseNumericInput, toAsciiDigits, toPersianDigits as digits } from './numericInput';
import './publicCalculator.css';

const money = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 0 });
const weight = new Intl.NumberFormat('fa-IR', { maximumFractionDigits: 3 });

export function QuoteStatus({ market, compact = false }) {
  const { quote, connection, loading, refresh } = market;
  return <div className={`qcl-live-quote is-${connection} ${compact ? 'is-compact' : ''}`}>
    <div className="qcl-live-quote__heading"><span className="qcl-live-dot" aria-hidden="true" />آخرین نرخ نقره</div>
    {quote ? <>
      <strong>{money.format(quote.sellPriceRial)} <span>ریال برای هر گرم نقره</span></strong>
      <p>آخرین به‌روزرسانی قیمت: <time>{formatPublishedQuoteTime(quote.publishedAt)}</time></p>
      {connection === 'retained' && <p role="status">الان اتصال برقرار نیست؛ محاسبه با آخرین نرخی انجام می‌شه که دریافت کردیم.</p>}
    </> : <p role="status">{loading ? 'داریم نرخ نقره رو می‌گیریم…' : 'فعلاً نرخ به دستمون نرسیده. دوباره امتحان کنید.'}</p>}
    {!compact && <button type="button" className="qcl-quiet-button" disabled={loading} onClick={refresh}>{loading ? 'در حال دریافت…' : 'بررسی دوبارهٔ نرخ'}</button>}
  </div>;
}

export default function PublicCalculator({ market }) {
  const [mode, setMode] = useState('amount');
  const inputRef = useRef(null);
  const pendingCaret = useRef(null);
  const [amount, setAmount] = useState(digits(DEMO.defaultAmountRial));
  const [grams, setGrams] = useState('۲');
  const inputId = useId();
  const input = mode === 'amount' ? amount : grams;
  const parsedInput = parseNumericInput(input);
  const displayInput = mode === 'amount' && parsedInput.valid ? numericDisplay(parsedInput.value).replace(/,/g, '٬') : digits(input);
  // Keep the caret next to the same digit when grouping inserts a separator.
  useLayoutEffect(() => {
    const position = pendingCaret.current;
    pendingCaret.current = null;
    if (position === null || mode !== 'amount' || document.activeElement !== inputRef.current) return;
    const value = toAsciiDigits(inputRef.current.value);
    let cursor = 0;
    let digitsSeen = 0;
    while (cursor < value.length && digitsSeen < position) {
      if (/\d/.test(value[cursor])) digitsSeen += 1;
      cursor += 1;
    }
    inputRef.current.setSelectionRange(cursor, cursor);
  }, [amount, mode]);
  const changeInput = event => {
    const raw = event.target.value;
    if (mode === 'weight') { setGrams(raw); return; }
    const canonical = toAsciiDigits(raw).replace(/[,،٬]/g, '');
    const {inputType, data} = event.nativeEvent;
    const digitEdit = inputType?.startsWith('delete') || (/^insert(Text|CompositionText)$/.test(inputType || '') && /^[0-9۰-۹٠-٩]$/.test(data || ''));
    if ((parseNumericInput(raw).valid || digitEdit) && /^\d*$/.test(canonical)) {
      pendingCaret.current = toAsciiDigits(raw.slice(0, event.target.selectionStart)).replace(/\D/g, '').length;
      setAmount(canonical);
    } else { pendingCaret.current = null; setAmount(raw); }
  };
  const deleteAcrossSeparator = event => {
    if (mode !== 'amount') return;
    const field = event.currentTarget;
    const start = field.selectionStart;
    if (start !== field.selectionEnd) return;
    if (event.key === 'Backspace' && start > 1 && /[,،٬]/.test(field.value[start - 1])) field.setSelectionRange(start - 2, start);
    if (event.key === 'Delete' && /[,،٬]/.test(field.value[start] || '')) field.setSelectionRange(start, start + 2);
  };
  const price = market.quote?.sellPriceRial;
  const result = useMemo(() => demoConversion(mode, input, price), [mode, input, price]);
  const tooPrecise = mode === 'weight' && (canonicalNumber(input).split('.')[1]?.length || 0) > 3;
  const sliderMin = mode === 'amount' ? 100000 : 0.001;
  const sliderBaseMax = mode === 'amount' ? 1000000000 : 100;
  const numericValue = Number(canonicalNumber(input));
  const sliderMax = Math.max(sliderBaseMax, Number.isFinite(numericValue) ? numericValue : 0);
  const sliderValue = Number.isFinite(numericValue) ? Math.max(sliderMin, Math.min(sliderMax, numericValue)) : sliderMin;
  const sliderProgress = ((sliderValue - sliderMin) / (sliderMax - sliderMin)) * 100;
  const updateValue = value => (mode === 'amount' ? setAmount(String(value)) : setGrams(String(value)));
  const invalid = Boolean(price) && input.trim() !== '' && (!result.valid || tooPrecise);
  return <section className="qcl-calculator" aria-labelledby="qcl-calculator-title">
    <div className="qcl-calculator__copy">
      <p className="qcl-kicker"><HeadingArt name="silver"/>با نرخ واقعی حساب کنید</p>
      <h3 id="qcl-calculator-title">با این مبلغ، چقدر نقره می‌تونید بخرید؟</h3>
      <p>مبلغ یا وزن دلخواهتون رو وارد کنید؛ نتیجه با آخرین نرخ نقره حساب می‌شه.</p>
      <div className="qcl-calculator__rate-art"><QuoteStatus market={market} /><div className="qcl-calculator__art"><PastelIllustration asset="16-section-silver-calculator" alt="تصویر محاسبهٔ نقره" sizes="(min-width: 1200px) 260px, (min-width: 760px) 22vw, (max-width: 640px) 180px, 240px" /></div></div>
    </div>
    <div className="qcl-calculator__panel">
      <div className="qcl-segmented" role="group" aria-label="روش محاسبه">
        <button type="button" aria-pressed={mode === 'amount'} onClick={() => setMode('amount')}>مبلغ ریالی</button>
        <button type="button" aria-pressed={mode === 'weight'} onClick={() => setMode('weight')}>وزن نقره</button>
      </div>
      <label htmlFor={inputId}>{mode === 'amount' ? 'مبلغ دلخواه به ریال' : 'وزن دلخواه به گرم'}</label>
      <div className="qcl-input-wrap"><input id={inputId} type="text" inputMode={mode === 'amount' ? 'numeric' : 'decimal'} ref={inputRef} value={displayInput} onKeyDown={deleteAcrossSeparator} aria-invalid={invalid || undefined} onChange={changeInput} /><span>{mode === 'amount' ? 'ریال' : 'گرم'}</span></div>
      <div className="qcl-silver-drag">
        <div className="qcl-silver-drag__heading"><span>بکشین و نتیجه رو ببینین</span><span aria-hidden="true">↔</span></div>
        <input type="range" aria-label={mode === 'amount' ? 'تغییر مبلغ با کشیدن نوار' : 'تغییر وزن با کشیدن نوار'} min={sliderMin} max={sliderMax} step={mode === 'amount' ? 100000 : 0.001} value={sliderValue} aria-valuetext={mode === 'amount' ? `${money.format(sliderValue)} ریال` : `${weight.format(sliderValue)} گرم`} style={{'--silver-progress': `${sliderProgress}%`}} onChange={event => updateValue(event.target.value)} />
        <div className="qcl-silver-drag__limits"><span>{mode === 'amount' ? `${money.format(sliderMin)} ریال` : '۰٫۰۰۱ گرم'}</span><span>{mode === 'amount' ? `${money.format(sliderMax)} ریال` : `${weight.format(sliderMax)} گرم`}</span></div>
        <div className="qcl-silver-drag__presets" role="group" aria-label="انتخاب سریع مقدار">
          {(mode === 'amount' ? [10000000, 50000000, 100000000] : [1, 5, 10]).map(value => <button key={value} type="button" aria-pressed={numericValue === value} onClick={() => updateValue(value)}>{mode === 'amount' ? `${money.format(value)} ریال` : `${weight.format(value)} گرم`}</button>)}
        </div>
      </div>
      {price ? result.valid && !tooPrecise ? <div className="qcl-calculator__result" aria-live="polite"><span>{mode === 'amount' ? 'معادل تقریبی وزن نقره' : 'معادل مبلغ'}</span><strong>{mode === 'amount' ? (result.gram > 0 && result.gram < 0.001 ? 'کمتر از ۰٫۰۰۱ گرم' : `${weight.format(result.gram)} گرم`) : `${money.format(result.amountRial)} ریال`}</strong></div> : <p className="qcl-input-error">{mode === 'amount' ? 'یک مبلغ کامل به ریال و بیشتر از صفر وارد کنید.' : 'وزن رو بیشتر از صفر و با حداکثر ۳ رقم اعشار وارد کنین؛ هر سوت برابر ۰٫۰۰۱ گرمه.'}</p> : <p className="qcl-calculator__fineprint">به‌محض دریافت نرخ، نتیجه رو اینجا می‌بینید.</p>}
      <details className="qcl-calculator__fineprint qcl-calculator__info"><summary><Icon name="info" size={16}/>دربارهٔ محاسبه و هزینه‌ها</summary><p>نرخ واقعیه؛ اینجا فقط محاسبه می‌کنیم و سفارشی ثبت نمی‌شه. هزینه‌های احتمالی جدا از این محاسبه‌ان. مبلغ نهایی رو پیش از تأیید سفارش در حسابتون می‌بینید.</p></details>
    </div>
  </section>;
}
